import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { supabase, getPerfil, getPermissoes } from '../lib/supabase'
import { permissoesComDefaults, acoesComDefaults, estadosAgendaComDefaults, estadosRelatorioComDefaults, estadosFaturacaoComDefaults } from '../lib/permissoes'
import { ORDEM_ESTADOS } from '../lib/estados'
import { useTenant } from './TenantContext'

const AuthContext = createContext(null)

export function AuthProvider({ children }) {
  const { tenant } = useTenant()
  const [utilizador, setUtilizador] = useState(null)
  const [perfil, setPerfil] = useState(null)
  const [carregando, setCarregando] = useState(true)
  // Permissões por papel (páginas + ações). Começa com os defaults para nunca
  // bloquear indevidamente enquanto carrega.
  const [permissoes, setPermissoes] = useState(() => ({
    paginas: permissoesComDefaults(null),
    acoes: acoesComDefaults(null),
    estadosAgenda: estadosAgendaComDefaults(null),
    estadosRelatorio: estadosRelatorioComDefaults(null),
    estadosFaturacao: estadosFaturacaoComDefaults(null),
  }))
  const utilizadorIdRef = useRef(null)

  // Carregar as permissões do tenant (são por espaço, não por utilizador).
  useEffect(() => {
    if (!tenant) return
    let ativo = true
    getPermissoes(tenant.id)
      .then(p => { if (ativo) setPermissoes(p) })
      .catch(() => { /* mantém os defaults */ })
    return () => { ativo = false }
  }, [tenant])

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      const user = session?.user ?? null
      utilizadorIdRef.current = user?.id ?? null
      setUtilizador(user)
      if (user && tenant) {
        carregarPerfil(user.id, { comLoading: true })
      } else {
        setCarregando(false)
      }
    })

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      const novoId = session?.user?.id ?? null

      if (novoId === utilizadorIdRef.current) {
        // Mesmo utilizador: refresh de token ou sincronização entre tabs.
        // Actualiza o token em memória sem desmontar a árvore.
        setUtilizador(session?.user ?? null)
        return
      }

      utilizadorIdRef.current = novoId

      if (novoId) {
        // Login com utilizador diferente — carregar perfil sem loading global.
        setUtilizador(session.user)
        carregarPerfil(novoId, { comLoading: false })
      } else {
        // Logout
        setUtilizador(null)
        setPerfil(null)
      }
    })

    return () => subscription.unsubscribe()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenant])

  async function carregarPerfil(userId, { comLoading = false } = {}) {
    if (!tenant) return
    if (comLoading) setCarregando(true)
    try {
      // Cliente com header x-tenant-id (ver getPerfil) — o set_tenant RPC
      // não persistia entre pedidos e o RLS devolvia 406 em loop.
      const data = await getPerfil(tenant.id, userId)
      setPerfil(data)
    } catch {
      setPerfil(null)
    } finally {
      if (comLoading) setCarregando(false)
    }
  }

  async function recarregarPerfil() {
    if (!utilizador || !tenant) return
    try {
      const data = await getPerfil(tenant.id, utilizador.id)
      setPerfil(data)
      return data
    } catch {
      setPerfil(null)
    }
  }

  async function logout() {
    await supabase.auth.signOut()
    setPerfil(null)
  }

  async function recarregarPermissoes() {
    if (!tenant) return
    try {
      const p = await getPermissoes(tenant.id)
      setPermissoes(p)
      return p
    } catch { /* mantém o atual */ }
  }

  const role = perfil?.role ?? null

  // O Super Admin tem sempre acesso; entradas não geridas ficam permitidas.
  function permitido(grupo, chave) {
    // 'promocoes' está desativada para todos os papéis, incluindo Super Admin
    // (esconde a aba no menu e bloqueia a rota /admin/promocoes por URL direto).
    if (grupo === 'paginas' && chave === 'promocoes') return false
    if (role === 'superuser') return true
    const e = permissoes?.[grupo]?.[chave]
    if (!e) return true
    if (role === 'manager') return !!e.manager
    if (role === 'funcionario') return !!e.funcionario
    return false
  }

  // Pode aceder a uma página? Pode executar uma ação/funcionalidade?
  function podeAceder(chave) { return permitido('paginas', chave) }
  function podeFazer(chave) { return permitido('acoes', chave) }

  // Conjunto de estados de marcação que o papel atual pode ver, para um grupo
  // ('estadosAgenda', 'estadosRelatorio' ou 'estadosFaturacao').
  // Super Admin vê sempre todos.
  function estadosVisiveis(grupo) {
    const mapa = permissoes?.[grupo] || {}
    return new Set(ORDEM_ESTADOS.filter(e => {
      if (role === 'superuser') return true
      const cfg = mapa[e]
      if (!cfg) return true
      if (role === 'manager') return !!cfg.manager
      if (role === 'funcionario') return !!cfg.funcionario
      return false
    }))
  }
  const estadosVisiveisAgenda = useMemo(
    () => estadosVisiveis('estadosAgenda'),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [permissoes, role]
  )
  const estadosVisiveisRelatorio = useMemo(
    () => estadosVisiveis('estadosRelatorio'),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [permissoes, role]
  )
  const estadosVisiveisFaturacao = useMemo(
    () => estadosVisiveis('estadosFaturacao'),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [permissoes, role]
  )

  const valor = {
    utilizador,
    perfil,
    role,
    isSuper: role === 'superuser',
    isManager: role === 'manager' || role === 'superuser',
    isFuncionario: role === 'funcionario',
    permissoes,
    podeAceder,
    podeFazer,
    estadosVisiveisAgenda,
    estadosVisiveisRelatorio,
    estadosVisiveisFaturacao,
    recarregarPermissoes,
    carregando,
    logout,
    recarregarPerfil,
  }

  return (
    <AuthContext.Provider value={valor}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth deve ser usado dentro de AuthProvider')
  return ctx
}
